import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useInOrOutStore } from "../../store/InOrOutStore";
import { PriceData } from "@bracket/in-or-out-server";
import { ChartPriceLabel } from "./ChartPriceLabel";
import { ChartDot } from "./ChartDot";
import { ChartCustomXAxis } from "./ChartCustomXAxis";
import { Button } from "@bracket/ui";

// Constants
const CHART_LENGTH_SEC = 30;
const FRAMES = 25;
const ITEMS_LIMIT = CHART_LENGTH_SEC * FRAMES - 300;
const UPDATE_DELAY = 1000 / FRAMES;

const REFERENCE_LINES_RANGE = 0.25;
const DOMAIN_RANGE = 0.12;

export const Chart = () => {
  const {
    history,
    previousResult,
    currentResult,
    currentInOutRange = 0.1,
    activeGameState,
  } = useInOrOutStore();

  const inOutRange = activeGameState
    ? activeGameState.inOutRange
    : currentInOutRange;

  const domainRange = inOutRange / 2;

  const updates = useRef(0);

  const [currentTime, setCurrentTime] = useState(Date.now());

  const [filteredHistory, setFilteredHistory] = useState<PriceData[]>([]);

  const [lineColor, setLineColor] = useState("#03FF8F"); // Default line color

  const gameOn = !!activeGameState;

  const prevReferenceLines = useRef({
    lower: 0,
    upper: 0,
  });

  const { referenceLines, dynamicDomain } = useMemo(() => {
    const price = activeGameState?.startPrice
      ? activeGameState.startPrice
      : filteredHistory.length
      ? filteredHistory[filteredHistory.length - 1].price
      : 0;

    const referenceLines = {
      lower: price - inOutRange,
      upper: price + inOutRange,
    };

    prevReferenceLines.current = referenceLines;

    const dynamicDomain = {
      min: price - inOutRange - domainRange,
      max: price + inOutRange + domainRange,
    };

    return { dynamicDomain, referenceLines };
  }, [filteredHistory, gameOn, activeGameState?.startPrice]);

  const chartMinTime = currentTime - (CHART_LENGTH_SEC - 10) * 1000;
  const chartMaxTime = currentTime + (CHART_LENGTH_SEC - 20) * 1000;

  const formatRelativeTime = (time) => {
    const diff = Date.now() - time;
    const seconds = Math.floor(diff / 1000);
    return `-${seconds} sec`;
  };

  useEffect(() => {
    updates.current = 0;
  }, [history]);

  useEffect(() => {
    if (!previousResult || !currentResult) {
      return;
    }

    const startTime = Date.now(); // Start time of this interpolation cycle
    const totalDuration = currentResult.time - previousResult.time;

    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime; // Time elapsed in this cycle

      const interpolationFactor = Math.min(elapsedTime / totalDuration, 1);

      const interpolatedPrice =
        previousResult.price +
        interpolationFactor * (currentResult.price - previousResult.price);

      const interpolatedTime =
        previousResult.time +
        interpolationFactor * (currentResult.time - previousResult.time);

      const newDataPoint = {
        time: interpolatedTime,
        price: interpolatedPrice,
      };

      setFilteredHistory((prevData) => {
        const newData = [...(prevData || []), newDataPoint];
        return newData.slice(-ITEMS_LIMIT);
      });

      setCurrentTime(Date.now());

      // Switch to the next result if the interpolation is complete
      if (interpolationFactor >= 1) {
        clearInterval(interval);
      }
    }, UPDATE_DELAY);

    return () => {
      clearInterval(interval);
    };
  }, [previousResult, currentResult]);

  useEffect(() => {
    if (gameOn && filteredHistory.length > 0) {
      const lastPrice = filteredHistory[filteredHistory.length - 1].price;

      let relativePosition = Math.abs(
        ((lastPrice - referenceLines.lower) /
          (referenceLines.upper - referenceLines.lower)) *
          2 -
          1
      );

      // const easeInExpo =
      //   relativePosition === 0 ? 0 : Math.pow(2, 10 * (relativePosition - 1));

      const easeIn = relativePosition * relativePosition;

      const r = Math.round(3 + easeIn * 225);
      const g = Math.round(225 - 208 * easeIn);
      const b = Math.round(143 - 140 * easeIn);

      const color = `rgba(${r}, ${g}, ${b}, 1)`;

      setLineColor(color);
    } else {
      setLineColor("#03ff8f");
    }
  }, [filteredHistory, gameOn]);

  const getPositionForPrice = (price) => {
    const chartHeight = 340;
    const middlePx = 193;

    const percentage =
      (price - dynamicDomain.min) / (dynamicDomain.max - dynamicDomain.min);

    const pxFromCenter = (0.5 - percentage) * chartHeight;

    return middlePx + pxFromCenter;
  };

  const customDivPosition =
    filteredHistory.length > 0
      ? getPositionForPrice(filteredHistory[filteredHistory.length - 1].price)
      : 0;

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "transparent",
        minHeight: "400px",
        zIndex: 1111,
        position: "relative",
        overflow: "hidden",
        flex: 1,
        display: "flex",
        flexFlow: "column",
      }}
    >
      <ResponsiveContainer width={"100%"} height={"100%"} minHeight={"400px"}>
        <LineChart
          data={filteredHistory} // Use filtered history as the data source
          margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
        >
          <XAxis
            dataKey="time"
            domain={[chartMinTime, chartMaxTime]} // Set the X-axis domain manually
            tickFormatter={formatRelativeTime}
            minTickGap={50}
            scale="time"
            type="number"
            fontFamily="Space Mono"
            fontSize={16}
            style={{ display: "none" }}
          />

          <YAxis
            orientation="right"
            domain={[dynamicDomain.min, dynamicDomain.max]} // Set the Y-axis domain manually
            type="number"
            fontFamily="Space Mono"
            fontSize={16}
            allowDataOverflow
            tickFormatter={(value) => value.toFixed(2)}
          />
          <Line
            type="monotone"
            dataKey="price"
            stroke={lineColor}
            strokeWidth={3}
            isAnimationActive={false}
            dot={
              <ChartDot
                data={filteredHistory}
                gameOn={gameOn}
                color={lineColor}
              />
            }
            style={
              gameOn
                ? {
                    stroke: lineColor,
                    filter: `drop-shadow(0 0 10px ${lineColor})`,
                  }
                : {}
            }
          />

          {/* Dynamic Reference Line */}
          {filteredHistory.length > 0 && (
            <ReferenceLine
              y={filteredHistory[filteredHistory.length - 1].price}
              stroke={lineColor}
              strokeDasharray="3 3"
              strokeOpacity={0.5}
            />
          )}

          <ReferenceLine
            y={referenceLines.lower}
            stroke="#F14764"
            strokeDasharray="3 3"
            label={{
              position: "insideTopLeft",
              value: "Out of range",
              fill: "#F14764",
              fontSize: 14,
              offset: 20,
              fontFamily: "Space Mono",
            }}
          />
          <ReferenceLine
            y={referenceLines.upper}
            stroke="#F14764"
            strokeDasharray="3 3"
            label={{
              position: "insideBottomLeft",
              value: "Out of range",
              fill: "#F14764",
              fontSize: 14,
              offset: 20,
              fontFamily: "Space Mono",
            }}
          />
        </LineChart>
      </ResponsiveContainer>
      {!!customDivPosition && (
        <ChartPriceLabel
          top={customDivPosition}
          ethPrice={currentResult?.price?.toFixed(2)}
          color={lineColor}
        />
      )}
      <ChartCustomXAxis
        color={lineColor}
        percent={
          activeGameState?.timeLeft ? 110 - activeGameState?.timeLeft * 10 : 0
        }
      />
    </div>
  );
};
